import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, Collapse, List, Typography, Box, Popover, Tooltip } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { resetController } from '../../store/slices/tableController';
import { usePopover } from '../../hooks/use-popover';

const NavButton = ({ to, title, icon, collapsed, isActive, external }) => {
    const dispatch = useDispatch();

    const buttonProps = {
        component: Link,
        to,
        onClick: () => dispatch(resetController()),
        fullWidth: true,
        startIcon: icon,
        target: external ? '_blank' : '_self',
        sx: {
            fontWeight: 400,
            justifyContent: 'flex-start',
            lineHeight: 0,
            minWidth: 'fit-content',
            px: 1.25,
            py: 1.25,
            whiteSpace: 'nowrap',
            '& .MuiButton-startIcon': {
                color: isActive ? 'primary.main' : 'text.secondary',
                margin: 0,
            },
        },
        variant: 'text',
    };

    const content = (
        <Tooltip
            title={collapsed && (title || '')}
            placement="right-start"
            slotProps={{
                popper: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [5, -5],
                            },
                        },
                    ],
                },
            }}
        >
            <Button {...buttonProps}>
                {!collapsed && (
                    <Typography
                        sx={{
                            color: isActive ? 'primary.main' : 'text.secondary',
                            height: '20px',
                            ml: 1.25,
                            alignItems: 'center',
                            display: 'flex',
                        }}
                        variant="inherit"
                    >
                        {title}
                    </Typography>
                )}
            </Button>
        </Tooltip>
    );

    return <li>{content}</li>;
};

const LayoutItem = ({
    active,
    activeHref,
    external,
    href,
    icon: Icon,
    routes,
    onOpen,
    open: openMenu,
    title,
    collapsed,
}) => {
    const [anchorRef, open, handleOpen, handleClose] = usePopover();

    const handleToggle = () => {
        collapsed ? handleOpen() : onOpen();
    };

    useEffect(() => {
        if (collapsed) openMenu && onOpen();
        else open && handleClose();
    }, [collapsed]);

    useEffect(() => {
        handleClose();
    }, [activeHref]);

    if (routes?.length <= 0) return;

    if (routes) {
        return (
            <List disablePadding sx={{ width: '100%' }}>
                <li>
                    <Tooltip
                        title={collapsed && (title || '')}
                        placement="right-start"
                        slotProps={{
                            popper: {
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [5, -5],
                                        },
                                    },
                                ],
                            },
                        }}
                    >
                        <Button
                            ref={anchorRef}
                            onClick={handleToggle}
                            fullWidth
                            startIcon={<Icon />}
                            endIcon={
                                !collapsed &&
                                (openMenu ? (
                                    <ExpandMoreIcon color="action" fontSize="small" />
                                ) : (
                                    <ChevronRightIcon color="action" fontSize="small" />
                                ))
                            }
                            sx={{
                                fontWeight: 400,
                                justifyContent: 'flex-start',
                                lineHeight: 0,
                                minWidth: 'fit-content',
                                px: 1.25,
                                py: 1.25,
                                '& .MuiButton-startIcon': {
                                    color: active ? 'primary.main' : 'action.active',
                                    margin: 0,
                                },
                                '& .MuiButton-endIcon': {
                                    color: 'action.disabled',
                                    marginLeft: 'auto',
                                },
                                ':hover': {
                                    backgroundColor: 'transparent',
                                },
                            }}
                            variant="text"
                        >
                            {!collapsed && (
                                <Typography
                                    sx={{
                                        color: active ? 'primary.main' : 'text.secondary',
                                        ml: 1.25,
                                        height: '20px',
                                        alignItems: 'center',
                                        display: 'flex',
                                    }}
                                    variant="inherit"
                                >
                                    {title}
                                </Typography>
                            )}
                        </Button>
                    </Tooltip>
                </li>
                <Popover
                    anchorEl={anchorRef.current}
                    anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                    keepMounted
                    onClose={handleClose}
                    open={open}
                    PaperProps={{
                        sx: {
                            width: 260,
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '10px 20px',
                            marginLeft: '10px',
                        },
                    }}
                >
                    <List disablePadding>
                        {routes.map((route) => (
                            <NavButton
                                key={route.href}
                                to={route.href}
                                title={route.title}
                                isActive={activeHref === route.href}
                            />
                        ))}
                    </List>
                </Popover>
                {!collapsed && (
                    <Collapse in={openMenu} unmountOnExit>
                        <List disablePadding>
                            {routes.map((route) => (
                                <Box sx={{ pl: '20px' }} key={route.href}>
                                    <NavButton
                                        key={route.href}
                                        to={route.href}
                                        title={route.title}
                                        isActive={activeHref === route.href}
                                        collapsed={false}
                                    />
                                </Box>
                            ))}
                        </List>
                    </Collapse>
                )}
            </List>
        );
    }

    return (
        <NavButton
            to={href}
            title={title}
            icon={<Icon />}
            isActive={active}
            external={external}
            collapsed={collapsed}
        />
    );
};

export default LayoutItem;
